import React, { useRef, useState } from "react";
import styled from "styled-components";
import emailjs from "@emailjs/browser";
import { Alert, ThemeProvider, createTheme } from "@mui/material";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  @media (max-width: 960px) {
    padding: 0px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1350px;
  padding: 0px 0px 80px 0px;
  gap: 12px;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const Title = styled.div`
  font-size: 42px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 32px;
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 16px;
  }
`;

const ContactForm = styled.form`
  width: 95%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.card};
  padding: 32px;
  border-radius: 16px;
  box-shadow: rgba(23, 92, 230, 0.15) 0px 4px 24px;
  margin-top: 28px;
  gap: 12px;
`;

const ContactTitle = styled.div`
  font-size: 24px;
  margin-bottom: 6px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`;

const ContactInput = styled.input`
  flex: 1;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`;

const ContactInputMessage = styled.textarea`
  flex: 1;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`;

const ContactButton = styled.input`
  width: 100%;
  text-decoration: none;
  text-align: center;
  background: hsla(271, 100%, 50%, 1);
  background: linear-gradient(
    225deg,
    hsla(271, 100%, 50%, 1) 0%,
    hsla(294, 100%, 50%, 1) 100%
  );
  padding: 13px 16px;
  margin-top: 2px;
  border-radius: 12px;
  border: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 18px;
  font-weight: 600;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

const SERVICE_ID = "service_vqm12xa";
const TEMPLATE_ID = "template_la7064p";
const PUBLIC_KEY = "jocUFhKyehMFmQgYW";

const Contact = () => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null); // Store error message
  const [loading, setLoading] = useState(false);
  const form = useRef();

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Log form data
    const formData = new FormData(form.current);
    console.log("Form data:", Object.fromEntries(formData));

    emailjs
      .sendForm(SERVICE_ID, TEMPLATE_ID, form.current, PUBLIC_KEY)
      .then(
        (result) => {
          console.log("Email sent successfully:", result.text);
          setOpen(true);
          setError(null);
          form.current.reset();
        },
        (error) => {
          console.error("EmailJS error:", error);
          setError(error.text || "Failed to send message. Please try again.");
        }
      )
      .catch((err) => {
        console.error("Unexpected error:", err);
        setError("An unexpected error occurred. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const theme = createTheme({
    components: {
      MuiAlert: {
        styleOverrides: {
          root: {
            backgroundColor: (props) =>
              props.severity === "success" ? "#4caf50" : "#f44336",
            color: "#fff",
          },
        },
      },
    },
  });

  return (
    <Container>
      <Wrapper>
        <Title>Contact</Title>
        <Desc>
          Feel free to reach out to me for any questions or opportunities!
        </Desc>
        <ContactForm ref={form} onSubmit={handleSubmit}>
          <ContactTitle>Email Me 🚀</ContactTitle>
          <ContactInput
            placeholder="Your Email"
            name="user_email"
            type="email"
            required
          />
          <ContactInput placeholder="Your Name" name="from_name" required />
          <ContactInput placeholder="Subject" name="subject" />
          <ContactInputMessage
            placeholder="Message"
            rows="4"
            name="message"
            required
          />
          <ContactButton
            type="submit"
            value={loading ? "Sending..." : "Send"}
            disabled={loading}
          />
        </ContactForm>
        <ThemeProvider theme={theme}>
          {open && (
            <Alert
              onClose={() => setOpen(false)}
              severity="success"
              sx={{
                position: "fixed",
                bottom: "20px",
                right: "20px",
                zIndex: 1000,
                width: "auto",
                maxWidth: "90%",
              }}
            >
              Your message was sent successfully!
            </Alert>
          )}
          {error && (
            <Alert
              onClose={() => setError(null)}
              severity="error"
              sx={{
                position: "fixed",
                bottom: "20px",
                right: "20px",
                zIndex: 1000,
                width: "auto",
                maxWidth: "90%",
              }}
            >
              {error}
            </Alert>
          )}
        </ThemeProvider>
      </Wrapper>
    </Container>
  );
};

export default Contact;
